<template>
  <div v-if="isLoading"
    class="absolute inset-0 z-50 flex items-center justify-center bg-white bg-opacity-75 rounded-lg">
    <div class="flex flex-col items-center gap-3">
      <div class="w-12 h-12 border-b-2 rounded-full animate-spin border-primary"></div>
      <span class="font-medium text-gray-600">Updating order...</span>
    </div>
  </div>

  <div v-if="start_download_production_file" style="display: none;">
    <ProductionFileGenerator
      :designUrl="activeProductionProps.designUrl"
      :sizesUrl="activeProductionProps.sizesUrl"
      :safeZoneUrl="activeProductionProps.safeZoneUrl"
      :groupPatterns="activeProductionProps.groupPatterns"
      :custom_logos="activeProductionProps.custom_logos"
      :texts="activeProductionProps.texts"
      :svgGroups="activeProductionProps.svgGroups"
      :logos="activeProductionProps.logos"
      :sizeDownload="activeProductionProps.sizeDownload"
      @resetDownloadProductionFile="resetDownloadProductionFile"
    />
  </div>

  <Tabs :initial-tab="computedProducts[0]?.id" :tabs="computedProducts.map((product) => ({
          id: product.id.toString(),
          label: product.new_product_name ?? product.product_name ?? 'No Product Name',
        }))
      "
      @change="onTabChange"
      :show-button="computedProducts.length > 1"
  >
    <template v-for="product in computedProducts" #[product.id]>
      <template v-if="product && order && order.id">
        <p class="text-center text-[20px] font-semibold">Order Details</p>
        <p class="break-words text-[22px] font-semibold">
           {{ product.new_product_name ?? product.product_name ?? 'No Product Name' }}
        </p>
        <div
          class="grid grid-cols-3 gap-4 mx-2 pt-7 border-[1px] border-solid border-[#eeeeee] bg-[#F9F9F9] rounded-xl p-4 relative">
          <button v-if="can('update-merchant-from-techpack') && IsCustomTechpackUploaded(order) === false"
            @click="startCompanyEdit" class="absolute top-2 right-2"
            :class="isEditingCompany ? 'opacity-50 cursor-not-allowed' : ''" :disabled="isEditingCompany">
            <PencilSquareIcon class="w-5 h-5" />
          </button>
          <div>
            <p class="text-[#5F5F5F] text-base selection">Company:</p>
            <p class="font-semibold break-words" v-if="!isEditingCompany">
              {{ order.company_name }}
            </p>

            <Select v-else append-to-body v-model="selectedCompany" :options="companies"
              :reduce="(company) => company.id" label="company_name" placeholder="Select Merchant" :clearable="false"
              @update:modelValue="onCompanyChange" />
          </div>

          <div>
            <p class="text-[#5F5F5F] text-base selection">Customer:</p>
            <p class="font-semibold break-words" v-if="!isEditingCompany">
              {{ getFullCustomerName(order?.customer?.first_name, order?.customer?.last_name) }}
            </p>
            <Select v-else append-to-body v-model="selectedCustomer" :options="customers" :reduce="(cust) => cust.id"
              label="name" :clearable="false" placeholder="Select Customer" @update:modelValue="onCustomerChange" >
            <template #option="{name, email}">
              <span v-if="isNullOrEmpty(name)">{{ email }}</span>
              <span v-else>{{ name }}</span>
            </template>
            </Select>
          </div>

          <div>
            <p class="text-[#5F5F5F] text-base selection">Email:</p>
            <p class="font-semibold break-words" v-if="!isEditingCompany">
              {{ order.customer.email }}
            </p>
            <input v-else type="text" v-model="selectedEmail" disabled
              class="w-full p-[9px] border border-gray-300 rounded-[4px] bg-gray-200 cursor-not-allowed" />
          </div>


          <!-- Customer Reference No -->
          <div>
            <p class="text-[#8e7575] text-base selection">Customer Reference No.</p>
            <p class="font-semibold break-words" v-if="!isEditingCompany">
              {{ order.customer_reference_no ?? 'N/A' }}
            </p>
            <input v-else type="text" v-model="selectedReferenceNo"
              class="w-full p-[9px] border border-gray-300 rounded-[4px]" placeholder="Enter Reference No." />
          </div>

          <!-- PO Number -->
          <div>
            <p class="text-[#5F5F5F] text-base selection">PO Num:</p>
            <p class="font-semibold break-words" v-if="!isEditingCompany">
              {{ order.additional_fields?.['po_number'] ?? 'N/A' }}
            </p>
            <input v-else type="text" v-model="selectedPoNumber"
              class="w-full p-[9px] border border-gray-300 rounded-[4px]" placeholder="Enter PO Number" />
          </div>

          <!-- Shipping Address Section -->
          <template v-if="showing_shipping_address_section">
            <div class="w-full col-span-3 p-6 mt-6 bg-white border shadow-sm rounded-xl">
              <h2 class="pb-2 mb-4 text-lg font-semibold text-gray-800 border-b">
                Shipping Address
              </h2>

              <!-- Existing Address Select -->
              <div class="mb-4">
                <label class="block mb-1 text-sm font-medium text-gray-700">
                  Choose Existing Address <span class="text-red-500">*</span>
                </label>

                <Select append-to-body v-model="shipping_address" :options="customerAddresses" label="address1"
                  placeholder="Select Address" :clearable="false" :disabled="is_custom_address"
                  :class="{ 'border-red-500': errorMessages['shipping_address'] }" />

                <span v-if="errorMessages['shipping_address']" class="text-xs text-red-500">
                  {{ errorMessages['shipping_address'] }}
                </span>
              </div>

              <!-- Toggle -->
              <div class="mb-4">
                <label class="inline-flex items-center cursor-pointer">
                  <Switch v-model="is_custom_address" id="is_custom_address" name="is_custom_address"
                    title="Enter a new shipping address" type="checkbox" />
                  <!-- <span class="ml-2 text-sm font-medium text-gray-700">
                    Enter a new shipping address
                  </span> -->
                </label>
              </div>


              <!-- New Address Form -->
              <div v-if="is_custom_address" class="p-4 space-y-4 border rounded-lg bg-gray-50">
                <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <!-- First Name -->
                  <div>
                    <input v-model="custom_shipping_address.first_name" placeholder="First Name"
                      class="w-full p-[9px] border rounded-[4px]"
                      :class="{ 'border-red-500': errorMessages['first_name'] }" />
                    <span v-if="errorMessages['first_name']" class="text-xs text-red-500">
                      {{ errorMessages['first_name'] }}
                    </span>
                  </div>

                  <!-- Last Name -->
                  <div>
                    <input v-model="custom_shipping_address.last_name" placeholder="Last Name"
                      class="w-full p-[9px] border rounded-[4px]"
                      :class="{ 'border-red-500': errorMessages['last_name'] }" />
                    <span v-if="errorMessages['last_name']" class="text-xs text-red-500">
                      {{ errorMessages['last_name'] }}
                    </span>
                  </div>

                  <!-- Email -->
                  <div>
                    <input v-model="custom_shipping_address.email" placeholder="Email"
                      class="w-full p-[9px] border rounded-[4px]"
                      :class="{ 'border-red-500': errorMessages['email'] }" />
                    <span v-if="errorMessages['email']" class="text-xs text-red-500">
                      {{ errorMessages['email'] }}
                    </span>
                  </div>

                  <!-- Phone -->
                  <div>
                    <input v-model="custom_shipping_address.phone_number" placeholder="Phone Number"
                      class="w-full p-[9px] border rounded-[4px]"
                      :class="{ 'border-red-500': errorMessages['phone_number'] }" />
                    <span v-if="errorMessages['phone_number']" class="text-xs text-red-500">
                      {{ errorMessages['phone_number'] }}
                    </span>
                  </div>

                  <!-- Address 1 -->
                  <div class="md:col-span-2">
                    <input v-model="custom_shipping_address.address1" placeholder="Address Line 1"
                      class="w-full p-[9px] border rounded-[4px]"
                      :class="{ 'border-red-500': errorMessages['address1'] }" />
                    <span v-if="errorMessages['address1']" class="text-xs text-red-500">
                      {{ errorMessages['address1'] }}
                    </span>
                  </div>

                  <!-- Address 2 -->
                  <div class="md:col-span-2">
                    <input v-model="custom_shipping_address.address2" placeholder="Address Line 2"
                      class="w-full p-[9px] border rounded-[4px]" />
                  </div>

                  <!-- City -->
                  <div>
                    <input v-model="custom_shipping_address.city" placeholder="City"
                      class="w-full p-[9px] border rounded-[4px]"
                      :class="{ 'border-red-500': errorMessages['city'] }" />
                    <span v-if="errorMessages['city']" class="text-xs text-red-500">
                      {{ errorMessages['city'] }}
                    </span>
                  </div>

                  <!-- Zip Code -->
                  <div>
                    <input v-model="custom_shipping_address.zip_code" placeholder="Zip Code"
                      class="w-full p-[9px] border rounded-[4px]"
                      :class="{ 'border-red-500': errorMessages['zip_code'] }" />
                    <span v-if="errorMessages['zip_code']" class="text-xs text-red-500">
                      {{ errorMessages['zip_code'] }}
                    </span>
                  </div>

                  <!-- Country -->
                  <div>
                    <Select v-model="custom_shipping_address.country_id" :options="formattedCountries"
                      :reduce="(c) => c.country_id" label="name" placeholder="Select Country" class="w-full"
                      :class="{ 'border-red-500': errorMessages['country_id'] }" />
                    <span v-if="errorMessages['country_id']" class="text-xs text-red-500">
                      {{ errorMessages['country_id'] }}
                    </span>
                  </div>

                  <!-- State -->
                  <div>
                    <input v-model="custom_shipping_address.state" placeholder="State/Province"
                      class="w-full p-[9px] border rounded-[4px]"
                      :class="{ 'border-red-500': errorMessages['state'] }" />
                    <span v-if="errorMessages['state']" class="text-xs text-red-500">
                      {{ errorMessages['state'] }}
                    </span>
                  </div>

                  <div>
                    <input v-model="custom_shipping_address.company_name" placeholder="Company Name"
                      class="w-full p-[9px] border rounded-[4px]"
                      :class="{ 'border-red-500': errorMessages['company_name'] }" />
                    <span v-if="errorMessages['company_name']" class="text-xs text-red-500">
                      {{ errorMessages['company_name'] }}
                    </span>
                  </div>


                </div>

                <!-- Save Address -->
                <!-- Toggle -->
                <div class="mb-4">
                  <label class="inline-flex items-center cursor-pointer">
                    <Switch v-model="save_address" id="save_address" name="save_address" title="Save this address"
                      type="checkbox" />
                    <!-- <span class="ml-2 text-sm font-medium text-gray-700">
                      Save this address
                    </span> -->
                  </label>
                </div>
              </div>
            </div>
          </template>


          <template v-if="!isEditingCompany">
            <button v-if="!product.reorder_data && can('can-mark-as-reorder')"
              class="w-48 px-3 py-2 text-white rounded-lg bg-primary" @click="() => (markAsReorderModal = true)">
              <span class="flex items-center justify-center">
                <ArrowPathIcon class="inline-block mr-1 text-white size-5" />
                Mark as reorder
              </span>
            </button>
            <template v-else>
              <div>
                <p class="text-[#5F5F5F] text-base selection">Reorder Message:</p>
                <p class="font-semibold break-words">
                  {{ makeReorderMessage(product) }}
                </p>
              </div>
            </template>
          </template>
          <div class="flex justify-end col-span-3 gap-2 mt-3" v-if="isEditingCompany">
            <button @click="cancelCompanyChanges"
              class="px-2 py-2 text-sm font-medium text-white bg-gray-500 rounded-lg hover:bg-gray-600">
              Cancel
            </button>
            <button @click="saveCompanyChanges" class="px-3 py-2 text-white rounded-lg" :class="[
              isSavingCompany || !selectedCustomer || !selectedCompany
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary hover:bg-primary/80',
            ]" :disabled="isSavingCompany || !selectedCustomer || !selectedCompany">
              {{ isSavingCompany ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </div>


        <div class="flex flex-col mx-2 mt-3">
          <div class="w-full">
            <div
              class="w-100 mt-3 border-[1px] border-solid border-[#eeeeee] bg-[#F9F9F9] rounded-xl flex flex-col justify-center items-center p-4 aspect-video">
              <div class="grid grid-cols-2 w-[80%] gap-1 bg-white">
                  <img :src="storage_url + '/' + front_preview" :key="front_preview" alt="" />
                  <img :src="storage_url + '/' + back_preview" :key="back_preview" alt="" />
              </div>
              <div class="flex flex-wrap justify-center gap-2 px-3 pb-3 mt-4">
                <button class="btn" v-if="!loadingPreviewImages" @click="downloadImages(product)">
                    <PhotoIcon class="size-5 stroke-[2px] text-[#444444]" />
                  Download Preview Images
                </button>
                <button class="btn" v-else disabled>Please wait...</button>
                <a class="cursor-pointer btn" @click="getAwsURLFromOrder(order.id)" v-if="
                  can('download-customer-pdf') &&
                  order.additional_fields &&
                  !order.additional_fields.hasOwnProperty('is_manual_order')
                ">
                  <DocumentTextIcon class="size-5 stroke-[2px] text-[#444444]" />
                  Download PDF
                </a>
                <button class="btn" @click.prevent="() => batchDownloadAssets(product)" v-if="!batch_loading">
                    <FolderArrowDownIcon class="size-5 stroke-[2px] text-[#444444]" />
                      Batch Download Assets
                    </button>
                    <button class="btn" v-else>Loading...</button>

                <div>
                  <template v-if="order.order_no">
                    <a :href="`${API_URL}/order/${order.id}/product/${product.id}/export`"
                      :download="`order_${order.order_no}_product_${product.product_name}.xlsx`" class="btn">
                      <QueueListIcon class="size-5 stroke-[2px] text-[#444444]" />
                      Download Excel sheet
                    </a>
                  </template>
                  <template v-else>
                    <a :href="`${API_URL}/order/${order.id}/product/${product.id}/export`"
                      :download="`order_${getCurrentDatetime()}_product_${product.product_name || product.new_product_name}.xlsx`" class="btn">
                      <QueueListIcon class="size-5 stroke-[2px] text-[#444444]" />
                      Download Excel sheet
                    </a>
                  </template>
                </div>
              </div>
            </div>
            <div
              class="w-100 mt-3 border-[1px] border-solid border-[#eeeeee] bg-[#F9F9F9] rounded-xl flex flex-col justify-center items-center p-4 aspect-video">
              <h4 class="mb-2 text-2xl font-semibold">Production Images</h4>
              <template v-if="product && product.hasOwnProperty('production_url') && product.production_url">
                <template v-if="order.order_no">
                  <div @click="downloadProductionFile(product.svg_url, `${order.order_no}.svg`)">
                    <img v-if="product.svg_url" :src="`${storage_url}/${product.svg_url}`" class="production-image"
                      style="height: 100%; width: 100%" />
                    <span v-else v-html="production_content" class="production-image"></span>
                  </div>
                </template>
                <template v-else>
                  <div @click="downloadProductionFile(product.svg_url, `${getCurrentDatetime()}.svg`)">
                    <img v-if="product.svg_url" :src="`${storage_url}/${product.svg_url}`" class="production-image"
                      style="height: 100%; width: 100%" />
                    <span v-else v-html="production_content" class="production-image"></span>
                  </div>
                </template>
              </template>
              <div class="grid grid-cols-2 w-[80%] gap-1 bg-white"></div>
            </div>
          </div>
        </div>

        <div class="w-full my-5 order-detail-roster">
          <div class="flex items-center justify-between mb-4">
            <!-- Left side: Heading -->
            <h4 class="text-2xl font-semibold">Product Roster</h4>

            <!-- Right side: Action Buttons -->
            <div class="flex space-x-2" v-if="
              can('update-merchant-from-techpack') && IsCustomTechpackUploaded(order) === false
            ">
              <!-- View Mode -->
              <button v-if="!isEditing" class="btn-edit" @click="startEditing(product)">
                Edit
              </button>

              <!-- Edit Mode -->
              <div v-else class="flex space-x-2">
                <button @click="saveChanges(product)" class="px-3 py-2 text-white rounded-lg" :class="[
                  isSavingCompany
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary/80',
                ]" :disabled="isSavingCompany">
                  {{ isSavingCompany ? 'Saving...' : 'Save' }}
                </button>

                <button @click="copyRoster(product)" v-if="product &&
                    Object.prototype.hasOwnProperty.call(product, 'product_roster_detail') &&
                    product.product_roster_detail.length > 0" class="px-3 py-2 text-white rounded-lg bg-primary hover:bg-primary/80">
                  Copy Roster
                </button>
                <button @click="pasteRoster(product)" v-if="props.copiedRoster.isAnyRosterCopied && product && props.copiedRoster.copiedFromProductIndex != product.id &&
                    Object.prototype.hasOwnProperty.call(product, 'product_roster_detail')" class="px-3 py-2 text-white rounded-lg bg-primary hover:bg-primary/80">
                  Paste Roster
                </button>


                <button class="px-4 py-2 text-sm font-medium text-white bg-gray-500 rounded-lg hover:bg-gray-600"
                  @click="cancelEditing(product)">
                  Cancel
                </button>
                <button class="btn-edit" @click="addRow(product)">+ Add Item</button>
              </div>
            </div>
          </div>

          <template v-if="
            product &&
            Object.prototype.hasOwnProperty.call(product, 'product_roster_detail') &&
            product.product_roster_detail.length > 0
          ">
            <DataTableRoster :key="product.id + (isEditing ? '-edit' : '-view')" :data="product.product_roster_detail"
              :columns="roster_headers" :showPagination="product.product_roster_detail.length > 10"
              :show-actions-column="true" :total-items="product.product_roster_detail.length" :current-pagination-info="{
                page: 1,
                per_page: 10,
                sort_by: 'size',
                sort_order: 'asc',
                items_for: 'all',
                search: '',
              }">
              <!-- Row actions -->
              <template #actions="{ row }">
                <!-- Edit Mode -->
                <template v-if="isEditing">
                  <button class="btn btn-danger" @click="deleteRow(row, product)">Delete</button>
                </template>

                <!-- View Mode -->
                <template v-else-if="product.design_sizes_url">
                  <button class="btn btn-secondary" @click="getSizeProductionFile(row, product)">
                    Download SVG
                  </button>
                </template>
              </template>
            </DataTableRoster>
          </template>
        </div>

        <!-- product rosters -->

        <div class="w-full mt-2">
          <h4 class="mb-2 text-2xl font-semibold">Custom Names</h4>
          <DataTable :key="product.id" :data="product_texts" :columns="custom_text_headers"
            :showPagination="product_texts.length > 10" :total-items="product_texts.length" :current-pagination-info="{
              page: 1,
              per_page: 10,
              sort_by: 'size',
              sort_order: 'asc',
              items_for: 'all',
              search: '',
            }" />
        </div>
        <div class="w-full my-2">
          <h4 class="mb-2 text-2xl font-semibold">Custom Numbers</h4>
          <DataTable :key="product.id" :data="product_numbers" :columns="custom_text_headers"
            :showPagination="product_numbers.length > 10" :total-items="product_numbers.length"
            :current-pagination-info="{
              page: 1,
              per_page: 10,
              sort_by: 'size',
              sort_order: 'asc',
              items_for: 'all',
              search: '',
            }" />
        </div>
        <div class="w-full pt-2 my-2">
          <h4 class="mb-2 text-2xl font-semibold">Custom Logos</h4>
          <template v-if="product && product.custom_logos.length > 0">
            <DataTable :key="product.id" :data="custom_logos" :columns="custom_logos_headers"
              :showPagination="custom_logos.length > 10" :total-items="custom_logos.length" :current-pagination-info="{
                page: 1,
                per_page: 10,
                sort_by: 'size',
                sort_order: 'asc',
                items_for: 'all',
                search: '',
              }" />
          </template>
        </div>
        <div class="w-full pt-2 my-2">
          <h4 class="mb-2 text-2xl font-semibold">SVG Groups</h4>
          <template v-if="product && product.hasOwnProperty('svg_groups') && product.svg_groups.length > 0">
            <DataTable :key="product.id" :data="product.svg_groups" :columns="svg_group_headers"
              :showPagination="product.svg_groups.length > 10" :total-items="product.svg_groups.length"
              :current-pagination-info="{
                page: 1,
                per_page: 10,
                sort_by: 'size',
                sort_order: 'asc',
                items_for: 'all',
                search: '',
              }" />
          </template>
          <template v-else="">
            <div>
              <div class="w-full">No Custom Logos Exists</div>
            </div>
          </template>
        </div>
      </template>
    </template>
  </Tabs>
  <Transition name="fade">
    <Dialog
      v-if="showLogoHistoryDialog"
      sm
      title="Logo History"
      show-close-button
      :loading="logoHistoryLoading"
      @close-event="showLogoHistoryDialog = false, logoHistoryData = []"
    >
      <template #modal-content>
        <DataTable
          :data="logoHistoryFilteredData"
          :columns="logo_history_headers"
          :showPagination="false"
        />
      </template>
    </Dialog>
  </Transition>
  <Transition name="fade">
    <MarkAsReorderModal v-if="markAsReorderModal" :order_item_id="order_item_id" :product_id="productId"
      :order_no="order.order_no" :factory_product_id="currentFactoryProductId"
      @closeEvent="() => (markAsReorderModal = false)" @handleOrderUpdated="() => emits('handleOrderUpdated')" />
  </Transition>
</template>
<style>
.btn-edit {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #fff;
  color: #00b087;
  border-radius: 8px;
  border: 1px solid #00b087;
  padding: 0.5rem 1.2rem;
  font-weight: 500;
  font-size: 1rem;
  transition:
    background 0.18s,
    color 0.18s,
    box-shadow 0.18s,
    border 0.18s;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(60, 60, 60, 0.04);
}

.btn-edit:hover,
.btn-edit:focus {
  background: #00b087;
  color: #fff;
  border-color: #00b087;
  outline: none;
}
</style>

<script setup lang="ts">
import Dialog from '@/components/Shared/Dialog.vue';
import DataTable from '@/components/Shared/DataTable.vue';
import DataTableRoster from '@/components/Shared/DataTableRoster.vue';
import Select from '@/components/Shared/Select.vue';
import Tabs from '@/components/Shared/Tabs.vue';
import { can, getCurrentDatetime, getFullCustomerName, isNullOrEmpty } from '@/composables';
import { getAwsURLFromOrder } from '@/composables/index';
import { showToast,showConfirm } from '@/composables/swal';
import Switch from '@/components/Shared/Switch.vue';
import { useProductDetails } from '@/composables/useProductDetails';
import { AddressModel, Country, FactoryProduct, OrderDetail } from '@/models';
import type { OrderItemOrderLine } from '@/models/OrderItemOrderLine';
import { normalizeOrderLinesFromApi, orderLineUsesFixedPricing } from '@/models/OrderItemOrderLine';
import {
  ArrowPathIcon,
  DocumentTextIcon,
  FolderArrowDownIcon,
  PhotoIcon,
  QueueListIcon,
} from '@heroicons/vue/24/outline';
import { PencilSquareIcon } from '@heroicons/vue/24/solid';
import { computed, onMounted, reactive, ref } from 'vue';
import MarkAsReorderModal from '../../Modals/MarkAsReorderModal.vue';
import { CopiedRoster } from '@/models/CopiedRoster';
import ProductionFileGenerator from '../../ProductionFile/ProductionFileGenerator.vue';


const props = defineProps<{
  factoryProducts: FactoryProduct[];
  order: OrderDetail;
  order_item_price?: number;
  order_item_id?: number;
  copiedRoster: CopiedRoster;
  /** JSON array of custom lines (legacy: single object). */
  order_line?: OrderItemOrderLine[] | OrderItemOrderLine | null;
}>();

const fmtMoney = (n: number | string | undefined | null) => {
  const v = typeof n === 'string' ? parseFloat(n) : Number(n);
  const x = Number.isFinite(v) ? v : 0;
  return x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtPercentDisplay = (n: number | string | undefined | null) => {
  const v = typeof n === 'string' ? parseFloat(String(n).replace(',', '.')) : Number(n);
  const x = Number.isFinite(v) ? v : 0;
  if (Number.isInteger(x)) return String(x);
  const s = x.toFixed(3).replace(/\.?0+$/, '');
  return s || '0';
};

const productsSubtotalSum = computed(() =>
  props.factoryProducts.reduce(
    (sum, p) => sum + (parseFloat(String((p as any)?.prices?.factory_product_total_price ?? 0)) || 0),
    0,
  ),
);


const orderLineRowAmount = (ol: OrderItemOrderLine) => {
  if (orderLineUsesFixedPricing(ol)) {
    return (Number(ol.quantity) || 0) * (Number(ol.price) || 0);
  }
  return (productsSubtotalSum.value * (Number(ol.percentage) || 0)) / 100;
};

const selectedReferenceNo = ref(props.order.customer_reference_no ?? '');
const markAsReorderModal = ref<boolean>(false);
const productId = ref<number>(0);
const currentFactoryProductId = ref<string>('');
const selectedPoNumber = ref(props.order.additional_fields?.['po_number'] ?? '');

const start_download_production_file = ref(false);
const activeProductionProps = ref<{
  designUrl: string;
  sizesUrl: string;
  safeZoneUrl: string;
  groupPatterns: Record<string, any>;
  custom_logos: any[];
  texts: any[];
  svgGroups: any[];
  logos: any[];
  sizeDownload: string;
}>({
  designUrl: '',
  sizesUrl: '',
  safeZoneUrl: '',
  groupPatterns: {},
  custom_logos: [],
  texts: [],
  svgGroups: [],
  logos: [],
  sizeDownload: '',
});

const emits = defineEmits(['openOrderPriceModal', 'handleOrderUpdated','onCopyRoster']);
onMounted(() => {
  loadOrderDetails(props.factoryProducts[0], false, props.order);
  productId.value = props.factoryProducts[0].product_id ?? null
  currentFactoryProductId.value = props.factoryProducts[0].id ?? null
});
const onTabChange = (tabId: string) => {
  const productIndex = props.factoryProducts.findIndex(
    (product) => product.id.toString() === tabId
  );
  if (productIndex !== -1) {
    loadOrderDetails(props.factoryProducts[productIndex], false, props.order);
    productId.value = props.factoryProducts[productIndex].product_id ?? null
    currentFactoryProductId.value = props.factoryProducts[productIndex].id ?? null
  }
};
const {
  product_numbers,
  product_texts,
  custom_logos,
  roster_headers,
  custom_text_headers,
  custom_logos_headers,
  svg_group_headers,
  API_URL,
  storage_url,
  batch_loading,
  loadingPreviewImages,
  front_preview,
  back_preview,
  computedProducts: rawComputedProducts = ref<FactoryProduct[]>([]),
  production_content,
  batchDownloadAssets,
  downloadImages,
  loadOrderDetails,
  getSizeSvg,
  downloadProductionFile,
  fetchCompanies,
  handleCompanyChange,
  handleUpdateOrderData,
  getProductSizes,
  isEditing,
  backupData,
  getCountries,
  makeReorderMessage,
  productSizes,
  showLogoHistoryDialog,
  logoHistoryLoading,
  logoHistoryData,
  logo_history_headers
} = useProductDetails(props);

const IsCustomTechpackUploaded = (order) => {
  return !!order.custom_techpack_pdf;
};
const computedProducts = ref(rawComputedProducts.value);
const isEditingCompany = ref(false);
const companies = ref<any[]>([]);
const customers = ref<any[]>([]);
const selectedCompany = ref(props.order?.company_id || null);
const selectedCustomer = ref(props.order?.customer?.id || null);
const selectedEmail = ref(props.order?.customer?.email || '');
const isSavingCompany = ref(false);

const original_company_id = ref(props.order?.company_id);
const original_customer_id = ref(props.order?.customer?.id);
const countries = ref<Country[]>([]);
const isLoading = ref(false);


const is_custom_address = ref(false)
const save_address = ref(false)
const showing_shipping_address_section = ref(false)
const shipping_address = ref<AddressModel | null>(null)
const customerAddresses = ref<AddressModel[]>([]);



const formattedCountries = computed(() => {
  return countries.value.map((country) => ({
    country_id: country.id,
    name: country.name,
  }));
});

const logoHistoryFilteredData = computed(() => {
  return logoHistoryData.value.filter((logoHistory) => logoHistory.order_no !== props.order.order_no)
});

// Reactive object to hold errors
const errorMessages = reactive<Record<string, string>>({})
const custom_shipping_address = reactive({
  first_name: '',
  last_name: '',
  email: '',
  address1: '',
  address2: '',
  phone_number: '',
  city: '',
  zip_code: '',
  country_id: '',
  company_name: '',
  state: '',
})
const validateShipping = () => {
  Object.keys(errorMessages).forEach(k => delete errorMessages[k])
  if (!is_custom_address.value) {
    if (!shipping_address.value) {
      errorMessages['shipping_address'] = 'Please select a shipping address'
      return false
    }
  } else {
    if (!custom_shipping_address.first_name) errorMessages['first_name'] = 'First name required'
    if (!custom_shipping_address.last_name) errorMessages['last_name'] = 'Last name required'
    if (!custom_shipping_address.email) errorMessages['email'] = 'Email required'
    if (!custom_shipping_address.address1) errorMessages['address1'] = 'Address required'
    if (!custom_shipping_address.city) errorMessages['city'] = 'City required'
    if (!custom_shipping_address.state) errorMessages['state'] = 'State required'
    if (!custom_shipping_address.zip_code) errorMessages['zip_code'] = 'Zip code required'
    if (!custom_shipping_address.country_id) errorMessages['country_id'] = 'Country required'
    if (!custom_shipping_address.phone_number) errorMessages['phone_number'] = 'Phone required'
    if (!custom_shipping_address.company_name) errorMessages['company_name'] = 'Company required'
    return Object.keys(errorMessages).length === 0
  }
  return true
}


onMounted(async () => {
  countries.value = await getCountries();
});

const startCompanyEdit = async () => {
  isEditingCompany.value = true;
  isLoading.value = true;
  companies.value = await fetchCompanies(); // fetch companies from API
  customers.value = await handleCompanyChange(selectedCompany.value);
  isLoading.value = false;
  //  On edit start, auto-select original customer if company matches
  if (selectedCompany.value === original_company_id.value) {
    const defaultCustomer = customers.value.find((c) => c.id === original_customer_id.value);
    if (defaultCustomer) {
      selectedCustomer.value = defaultCustomer.id;
      selectedEmail.value = defaultCustomer.email;
    }
  }
};

const onCompanyChange = async (companyId: number) => {
  isLoading.value = true;
  selectedCompany.value = companyId;


  customers.value = await handleCompanyChange(companyId);
  isLoading.value = false;
  //  If same company as original → restore original customer
  if (companyId === original_company_id.value) {
    const defaultCustomer = customers.value.find((c) => c.id === original_customer_id.value);
    if (defaultCustomer) {
      selectedCustomer.value = defaultCustomer.id;
      selectedEmail.value = defaultCustomer.email;
      return;
    }
  }
  //  If new company → reset
  selectedCustomer.value = null;
  selectedEmail.value = '';
  showing_shipping_address_section.value = false;
  save_address.value = false
  is_custom_address.value = false
};

const onCustomerChange = (customerId: number) => {
  const cust = customers.value.find((c) => c.id === customerId);
  selectedCustomer.value = cust?.id || null;
  selectedEmail.value = cust?.email || '';
  customerAddresses.value = cust?.addresses || [];
  shipping_address.value = null;
  showing_shipping_address_section.value = true;


  const defaultCustomer = selectedCustomer.value === original_customer_id.value && selectedCompany.value === original_company_id.value;
  if (defaultCustomer) {
    showing_shipping_address_section.value = false;
    save_address.value = false
    is_custom_address.value = false
  }
};


// Save company changes
const saveCompanyChanges = async () => {

  if (showing_shipping_address_section.value && !validateShipping()) return
  isSavingCompany.value = true
  try {
    const data: any = {
      order_id: props.order.id,
      company_id: selectedCompany.value,
      customer_id: selectedCustomer.value,
      customer_reference_no: selectedReferenceNo.value,
      po_number: selectedPoNumber.value,
      company_name: companies.value.find(c => c.id === selectedCompany.value)?.company_name || null,
      customer_name: customers.value.find(c => c.id === selectedCustomer.value)
        ? `${customers.value.find(c => c.id === selectedCustomer.value)!.first_name} ${customers.value.find(c => c.id === selectedCustomer.value)!.last_name}`
        : null,


    }

    if (showing_shipping_address_section.value) {
      if (is_custom_address.value) {
        data.is_custom_address = true
        data.custom_shipping_address = { ...custom_shipping_address }
        data.save_address = save_address.value
        data.shipping_address_id = null
      } else {
        data.is_custom_address = false
        data.custom_shipping_address = { ...shipping_address.value }
        data.shipping_address_id = shipping_address.value?.id
      }
      data.customer_shipping_address_changed = true
    }

    const response = await handleUpdateOrderData(data)
    if (response && response.success) {
      showToast('Order data updated successfully.', 'success');
      const cust = customers.value.find((c) => c.id === selectedCustomer.value);
      if (cust) {

        props.order.company_name = companies.value.find((c) => c.id === selectedCompany.value)?.company_name || '';
        props.order.customer = cust;
        props.order.customer_reference_no = selectedReferenceNo.value;
        props.order.additional_fields = {
          ...(props.order.additional_fields || {}),
          po_number: selectedPoNumber.value,
        };
        original_company_id.value = selectedCompany.value;
        original_customer_id.value = selectedCustomer.value;

      }

      isEditingCompany.value = false;
      showing_shipping_address_section.value = false;
      save_address.value = false
      is_custom_address.value = false
      emits('handleOrderUpdated');
    } else {
      showToast('Failed to update order. Please try again.', 'error');
    }

  } catch (err: any) {
    console.error('Error updating order data:', err);
    showToast(err.message, 'error')
  } finally {
    isSavingCompany.value = false
    // showing_shipping_address_section.value = false;
    // save_address.value = false
    // is_custom_address.value = false
  }
}

const cancelCompanyChanges = () => {
  isEditingCompany.value = false;
  // reset local selections
  selectedCompany.value = props.order?.company_id || null;
  selectedCustomer.value = props.order?.customer?.id || null;
  selectedEmail.value = props.order?.customer?.email || '';

  showing_shipping_address_section.value = false;
  save_address.value = false
  is_custom_address.value = false
};

// product roster update

const startEditing = async (product) => {
  isLoading.value = true;
  await getProductSizes(product.product_id);
  backupData.value = JSON.parse(JSON.stringify(product.product_roster_detail));
  isEditing.value = true;
  isLoading.value = false;
};

const cancelEditing = (product) => {
  product.product_roster_detail = JSON.parse(JSON.stringify(backupData.value));
  isEditing.value = false;
};

const saveChanges = async (product) => {
  isSavingCompany.value = true;
  isLoading.value = false;
  try {
    const data = {
      order_id: props.order.id,
      company_id: null,
      customer_id: null,
      order_item_id: props.order_item_id,
      factory_product_id: product.id,
      product_roster_detail: product.product_roster_detail,
    };

    const response = await handleUpdateOrderData(data);

    if (response && response.success) {
      showToast('Product roster updated successfully.', 'success');
      emits('handleOrderUpdated');
      // agar UI state update karna ho to yaha karo
    } else {
      showToast('Failed to update product roster. Please try again.', 'error');
      // revert changes on failure
      product.product_roster_detail = JSON.parse(JSON.stringify(backupData.value));
    }
  } catch (err: any) {
    showToast('Error updating product roster: ' + err.message, 'error');
    // revert changes on error
    product.product_roster_detail = JSON.parse(JSON.stringify(backupData.value));
  } finally {
    isEditing.value = false;
    backupData.value = [];
    isLoading.value = false;
    isSavingCompany.value = false;
  }
};

const addRow = (product) => {

  product.product_roster_detail = product.product_roster_detail || [];
  let lastProductSize = product.product_roster_detail.length > 0 ? product.product_roster_detail[product.product_roster_detail.length - 1].size : '';
  product.product_roster_detail.push({
    size: lastProductSize,
    color: '',
    quantity: 1,
    number: '',
    text: '',
  });

};

const deleteRow = (row, product) => {
  const index = product.product_roster_detail.findIndex((r) => r === row);
  if (index !== -1) {
    product.product_roster_detail.splice(index, 1);
  } else {
    console.warn('Row not found in product_roster_detail', row);
  }
};

const copyRoster = (product) => {
  if(props.copiedRoster.isAnyRosterCopied && props.copiedRoster.copiedFromProductIndex != product.id){
    showConfirm(
          'Overwrite existing copied roster?',
          'You already have a roster copied. Do you want to copy this product’s roster instead?',
        ).then((result) => {
          if (result) {
            setCopiedRoster(product);
            showToast('Roster copied successfully', 'success');
          }
        });
  }
  else{
    setCopiedRoster(product);
    showToast('Roster copied successfully', 'success');
  }
}

const setCopiedRoster =(product) => {
  let copiedRoster:CopiedRoster ={
      isAnyRosterCopied: true,
      copiedFromProductIndex: product.id,
      rosterData: product.product_roster_detail
    }
  emits('onCopyRoster',{copiedRoster: copiedRoster})
}

const pasteRoster = async (product) => {
  backupData.value = JSON.parse(JSON.stringify(product.product_roster_detail));

  let newRosterData = JSON.parse(JSON.stringify(props.copiedRoster.rosterData));
  newRosterData.map((player) => {
    let matchedSize = '';
    if(!product.is_custom_product && productSizes.value.length > 0){
      productSizes.value.forEach(size => {
          if(size.name == player.size) {
            matchedSize = size.name;
          }
        });
    }else if(product.is_custom_product){
      matchedSize = player.size;
    }

    player.size = matchedSize;
    return player;
  })

  product.product_roster_detail = newRosterData;
  showToast('Roster pasted successfully', 'success');
}

const getSizeProductionFile = (row: any, product: FactoryProduct) => {
  // Guard: can't generate size-specific file without the sizes SVG
  if (!product.design_sizes_url) {
    // Either show a toast or silently fall back — your call
    console.warn('design_sizes_url not available for this product');
    return;
  }

  const customTexts = product.product_custom_texts
    ? JSON.parse(JSON.stringify(product.product_custom_texts))
    : [];

  const nameIndex = customTexts.findIndex((t: any) => t.type === 'name');
  const numberIndex = customTexts.findIndex((t: any) => t.type === 'number');
  if (nameIndex !== -1) customTexts[nameIndex].value = row.text ?? '';
  if (numberIndex !== -1) customTexts[numberIndex].value = row.number ?? '';

  activeProductionProps.value = {
    designUrl: product.production_design_url ?? '',
    sizesUrl: product.design_sizes_url ?? '',
    safeZoneUrl: product.design_safezone_url ?? '',
    groupPatterns: Array.isArray(product.group_patterns)
      ? {}
      : (product.group_patterns ?? {}),
    custom_logos: product.custom_logos ?? [],
    texts: customTexts,
    svgGroups: product.svg_groups ?? [],
    logos: product.fixed_logos ?? [],
    sizeDownload: row.size ?? '',
  };
  console.log('Active production props set for size SVG generation:', activeProductionProps.value);
  start_download_production_file.value = true;
};

const resetDownloadProductionFile = () => {
  start_download_production_file.value = false;
};
</script>
<style lang="scss" scoped>
.btn {
  @apply px-2 py-2 flex gap-2 justify-between items-center rounded-lg capitalize text-[#444444] bg-[#EDEDED];
}
</style>
